export interface SourceBlock {
	page: number; // 1-indexed page number
	bbox: number[]; // [x0, y0, x1, y1] in PDF coords (bottom-left origin)
}

/** Simplified source reference with just IDs (persisted format) */
export interface SourceDocument {
	doc_id: number;
	chunk_ids: number[];
}

// ─── SecondarySource discriminated union ─────────────────────────────────────

export type SecondarySourcePlatform = 'web' | 'lobbyregister' | 'abgeordnetenwatch' | 'fragdenstaat';

export interface BaseSecondarySource {
	id: string; // CRC32 hex hash of URL (8 chars) — dedup key
	title: string;
	url: string;
	description: string;
	/** Missing on sources persisted before the platform field existed — treat as 'web' */
	platform?: SecondarySourcePlatform;
	fetched_at?: string;
}

export interface WebPageSource extends BaseSecondarySource {
	type: 'web' | 'fetched_url';
	content?: string;
}

export interface PoliticianSidejob {
	label: string;
	organization?: string;
	income_eur?: number;
	income_level?: string;
	date?: string;
	topics?: string[];
}

export interface PoliticianCommittee {
	name: string;
	role?: string;
	period?: string;
}

export interface PersonSource extends BaseSecondarySource {
	type: 'person';
	// abgeordnetenwatch
	party?: string;
	year_of_birth?: number;
	occupation?: string;
	education?: string;
	residence?: string;
	questions_received?: number;
	questions_answered?: number;
	fraction?: string;
	constituency?: string;
	/** How the mandate was won: 'constituency' | 'list' | 'moved_up' */
	mandate_won?: string;
	parliament_period?: string;
	sidejobs?: PoliticianSidejob[];
	committees?: PoliticianCommittee[];
	// lobbyregister individual
	register_number?: string;
	active?: boolean;
	activity_type?: string;
	activity_description?: string;
	fields_of_interest?: string[];
	lobby_methods?: string[];
	financial_expenses_eur?: { from: number; to: number };
	memberships?: string[];
	funding_sources?: string[];
	public_allowances?: string[];
}

export interface CompanySource extends BaseSecondarySource {
	type: 'company';
	register_number?: string;
	legal_form?: string;
	active?: boolean;
	activity_type?: string;
	activity_description?: string;
	fields_of_interest?: string[];
	lobby_methods?: string[];
	financial_expenses_eur?: { from: number; to: number };
	employee_fte?: number;
	legal_representatives?: string[];
	entrusted_persons?: string[];
	members_count?: { total: number; natural_persons?: number; organizations?: number; as_of?: string };
	memberships?: string[];
	funding_sources?: string[];
	public_allowances?: string[];
	legislative_projects?: string[];
	client_organizations?: string[];
	codex_violations?: boolean;
}

export interface IfgRequestSource extends BaseSecondarySource {
	type: 'ifg_request';
	/** FragDenStaat request id */
	external_id?: string;
	status?: string;
	resolution?: string;
	public_body?: string;
	jurisdiction?: string;
	created_at?: string;
	last_message?: string;
	due_date?: string;
	refusal_reason?: string;
	costs?: number;
	tags?: string[];
}

export interface PollSource extends BaseSecondarySource {
	type: 'poll';
	date?: string;
	accepted?: boolean | null;
	parliament?: string;
	topics?: string[];
	summary?: string;
}

export type SecondarySource = WebPageSource | PersonSource | CompanySource | IfgRequestSource | PollSource;

// Record keyed by the discriminant so the compiler enforces it stays in sync with the union
const SECONDARY_SOURCE_TYPES: Record<SecondarySource['type'], true> = {
	web: true,
	fetched_url: true,
	person: true,
	company: true,
	ifg_request: true,
	poll: true
};

export function isSecondarySource(value: object): value is SecondarySource {
	return 'type' in value && typeof value.type === 'string' && value.type in SECONDARY_SOURCE_TYPES;
}

/**
 * Resolve the secondary source of a cited source, or undefined for KB sources.
 * Conversations persisted before the SecondarySource union only carry flat web fields.
 */
export function resolveSecondarySource(source: CitedSource): SecondarySource | undefined {
	if (source.secondary_source) {
		return source.secondary_source;
	}
	if (source.web_id != null) {
		return {
			id: source.web_id,
			type: 'web',
			title: source.title,
			url: source.url ?? '',
			description: source.description ?? '',
			fetched_at: source.fetched_at
		};
	}
	return undefined;
}

/** A single citation occurrence (one badge) within a cited source */
export interface CitationOccurrence {
	hash: string;
	sub_index: string;
	quotedText?: string;
	chunkText?: string;
	sourceBlocks?: SourceBlock[];
	type: 'chunk' | 'document' | 'web';
	/** chunkId for 'chunk', docId for 'document', webId for 'web' */
	id: number | string;
}

/** Unified cited source — replaces QuotedDocument and QuotedWebSource */
export interface CitedSource {
	citation_index: number;
	citation_color: string;
	occurrences: CitationOccurrence[];
	title: string;
	// KB-specific
	doc_id?: number;
	chunk_ids?: number[];
	// Secondary source — full typed entity for detail rendering
	secondary_source?: SecondarySource;
	// Legacy flat fields kept for backward compat
	web_id?: string;
	url?: string;
	description?: string;
	fetched_at?: string;
}
